import Decimal from 'decimal.js'
import type { z } from 'zod'
import { billMoneySchema, billQuantitySchema, type billSchema } from '@super-mega/server/contracts/bill'

type Bill = z.infer<typeof billSchema>
export type BillIssueEntry = {
  mode: 'usage' | 'manual'
  quantity: string | null
  unitPrice: string | null
  amount: string | null
}

export function canEnterUtility(bill: Bill) {
  return bill.status === 'draft' && ['water', 'electricity'].includes(bill.feeType)
}

export function initialIssueEntry(bill: Bill): BillIssueEntry {
  return {
    mode:
      canEnterUtility(bill) && bill.meteredUnitPrice != null && (bill.totalAmount === null || bill.quantity != null)
        ? 'usage'
        : 'manual',
    quantity: bill.quantity ?? null,
    unitPrice: bill.meteredUnitPrice ?? null,
    amount: bill.totalAmount
  }
}

export function issueEntryAmount(bill: Bill, entry: BillIssueEntry) {
  if (entry.mode === 'manual') {
    return entry.amount
  }
  if (
    !canEnterUtility(bill) ||
    !billQuantitySchema.safeParse(entry.quantity).success ||
    !billMoneySchema.safeParse(entry.unitPrice).success
  ) {
    return null
  }
  return new Decimal(entry.quantity!).mul(entry.unitPrice!).toFixed(2, Decimal.ROUND_HALF_UP)
}

export function validIssueEntry(bill: Bill, entry: BillIssueEntry) {
  return billMoneySchema.safeParse(issueEntryAmount(bill, entry)).success
}

export function issueEntryPayload(bill: Bill, entry: BillIssueEntry) {
  if (bill.status !== 'draft') {
    return {}
  }
  if (entry.mode === 'usage') {
    const priceChanged = bill.meteredUnitPrice == null || !new Decimal(entry.unitPrice!).eq(bill.meteredUnitPrice)
    const amount = issueEntryAmount(bill, entry)
    const amountUnchanged = amount !== null && bill.totalAmount !== null && new Decimal(amount).eq(bill.totalAmount)
    // 金额、用量和单价都一致才直接出账，避免舍入单价后仍沿用旧金额。
    if (
      amountUnchanged &&
      bill.quantity != null &&
      entry.quantity != null &&
      new Decimal(entry.quantity).eq(bill.quantity) &&
      !priceChanged
    ) {
      return {}
    }
    return { issueQuantity: entry.quantity!, ...(priceChanged ? { issueUnitPrice: entry.unitPrice! } : {}) }
  }
  // 从计量切到定额时，即便总额相同，也清除原先的数量、单价。
  return entry.amount !== bill.totalAmount || (canEnterUtility(bill) && bill.quantity != null)
    ? { issueAmount: entry.amount! }
    : {}
}
